import type { FormApi } from "../../components/form/createForm.ts";
import { createForm } from "../../components/form/createForm.ts";
import { Form } from "../../components/form/Form.tsx";
import { boolean, type InferOutput, object, optional, pipe } from "valibot";
import { phoneNumber, requireText } from "../../utils/validation/base.ts";
import { MAX_CHARACTERS_SM, MINIMUM_ACTION_LOADING_DELAY } from "../../constants.ts";
import { TextInput } from "../../components/inputs/TextInput.tsx";
import PhoneNumberInput from "../../components/inputs/PhoneNumberInput.tsx";
import { useTranslation } from "../../state/i18n.tsx";
import { Checkbox } from "../../components/checkbox/Checkbox.tsx";
import type { StepFormProps } from "./OnboardingPage.tsx";
import { Dynamic } from "solid-js/web";
import PageHeader from "../PageHeader.tsx";
import moreappWhiteIcon from "../../assets/moreapp-icon-white.svg";
import Section from "../Section.tsx";
import UserService from "../../services/UserService.ts";
import { useUserState } from "../../state/user.tsx";
import pMinDelay from "p-min-delay";
import { createSignal } from "solid-js";

const UserSchema = pipe(
  object({
    firstName: requireText(MAX_CHARACTERS_SM),
    lastName: requireText(MAX_CHARACTERS_SM),
    phoneNumber: optional(phoneNumber()),
    receiveNewsLetter: boolean(),
  }),
);

const UserForm = (props: StepFormProps) => {
  const { t } = useTranslation();
  const user = useUserState();
  const patchUser = UserService.patchUser();
  const [showWarning, setShowWarning] = createSignal(false);

  const form = createForm<InferOutput<typeof UserSchema>>(
    UserSchema,
    async ({ firstName, lastName, phoneNumber, receiveNewsLetter }) => {
      await pMinDelay(
        patchUser.mutateAsync(
          [
            {
              op: "replace",
              path: "/settings/firstName",
              value: firstName,
            },
            {
              op: "replace",
              path: "/settings/lastName",
              value: lastName,
            },
            {
              op: "replace",
              path: "/settings/phoneNumber",
              value: phoneNumber,
            },
            {
              op: "replace",
              path: "/settings/receiveNewsLetter",
              value: receiveNewsLetter,
            },
          ],
          {
            onError: () => {
              setShowWarning(true);
            },
          },
        ),
        MINIMUM_ACTION_LOADING_DELAY,
      ).then(props.next);
    },
    user(),
  );

  return (
    <>
      <PageHeader
        title={t(($) => $.ONBOARDING_PAGE_USER_WELCOME)}
        subTitle={t(($) => $.ONBOARDING_PAGE_USER_SETUP_ACCOUNT)}
        logoSrc={moreappWhiteIcon}
      />
      <Section fullHeight>
        <h2 class="mb-6 text-header-xl text-primary">{t(($) => $.ONBOARDING_PAGE_USER_TITLE)}</h2>
        <Form form={form} class="flex size-full grow flex-col">
          {({ value, error, updateValue }) => (
            <div class="flex h-full grow flex-col">
              <div class="flex w-full flex-col gap-y-6">
                <TextInput
                  label={t(($) => $.ONBOARDING_PAGE_USER_FIRST_NAME)}
                  required
                  value={value("firstName")}
                  errorMessage={error("firstName")}
                  onInput={(e) => updateValue("firstName", e.target.value)}
                  onBlur={(e) => updateValue("firstName", e.target.value)}
                />
                <TextInput
                  label={t(($) => $.ONBOARDING_PAGE_USER_LAST_NAME)}
                  required
                  value={value("lastName")}
                  errorMessage={error("lastName")}
                  onInput={(e) => updateValue("lastName", e.target.value)}
                  onBlur={(e) => updateValue("lastName", e.target.value)}
                />
                <PhoneNumberInput
                  label={t(($) => $.ONBOARDING_PAGE_USER_PHONE_NUMBER)}
                  value={value("phoneNumber")}
                  errorMessage={error("phoneNumber")}
                  onChange={(phoneNumber) => updateValue("phoneNumber", phoneNumber)}
                />
                <Checkbox
                  checked={value("receiveNewsLetter") ?? false}
                  setChecked={(checked) => updateValue("receiveNewsLetter", checked)}
                  label={t(($) => $.ONBOARDING_PAGE_USER_NEWSLETTER)}
                />
              </div>
              <Dynamic
                component={props.navigation}
                form={form as unknown as FormApi<never>}
                back={props.back}
                showWarning={showWarning()}
              />
            </div>
          )}
        </Form>
      </Section>
    </>
  );
};

export default UserForm;
